import { useState, useEffect } from "react";
import { Upload, Trash2, Award, Shield, FileSignature, Save } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

export default function DocSettings() {
    const [settings, setSettings] = useState({
        company_logo: null,
        founder_signature: null,
        company_seal: null,
        founder_name: "Prakhar Sharma"
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const r = await api.get("/documents/settings");
            setSettings({
                company_logo: r.data.company_logo || null,
                founder_signature: r.data.founder_signature || null,
                company_seal: r.data.company_seal || null,
                founder_name: r.data.founder_name || "Prakhar Sharma"
            });
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e, field) => {
        const file = e.target.files[0];
        if (!file) return;

        // Check file size (limit base64 size to 2MB)
        if (file.size > 2 * 1024 * 1024) {
            toast.error("File is too large. Max size is 2MB.");
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setSettings((prev) => ({
                ...prev,
                [field]: reader.result
            }));
            toast.success(`${field.replace("_", " ")} uploaded successfully!`);
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveFile = (field) => {
        setSettings((prev) => ({
            ...prev,
            [field]: null
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.patch("/documents/settings", settings);
            toast.success("Document settings saved successfully!");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex justify-between items-center border-b border-slate-200 dark:border-white/10 pb-4">
                <div>
                    <h1 className="text-2xl font-heading font-bold text-slate-800 dark:text-slate-100 font-heading">Document Settings</h1>
                    <p className="text-sm text-muted-foreground">Upload official branding assets for PDFs and certificates.</p>
                </div>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 bg-primary hover:bg-primary-hover disabled:bg-primary/50 text-white px-5 py-2.5 rounded-xl transition shadow-md font-semibold text-sm"
                >
                    {saving ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                        <Save className="w-4 h-4" />
                    )}
                    <span>Save Settings</span>
                </button>
            </div>

            {/* Founder Name */}
            <div className="glass p-6 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm space-y-3">
                <h3 className="font-heading font-semibold text-slate-800 dark:text-slate-100 text-base">Signatory Details</h3>
                <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Founder / CEO Name</label>
                    <input
                        type="text"
                        value={settings.founder_name}
                        onChange={(e) => setSettings({ ...settings, founder_name: e.target.value })}
                        className="w-full max-w-md px-3.5 py-2.5 border border-slate-200 dark:border-white/10 rounded-xl bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                        placeholder="e.g. Prakhar Sharma"
                    />
                </div>
            </div>

            {/* Asset Upload Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                
                {/* Company Logo */}
                <div className="glass p-6 rounded-2xl border border-slate-200 dark:border-white/10 flex flex-col justify-between shadow-sm">
                    <div className="space-y-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary grid place-items-center">
                            <Award className="w-5 h-5" />
                        </div>
                        <div>
                            <h4 className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Company Logo</h4>
                            <p className="text-xs text-muted-foreground mt-0.5">Visible at the top of certificates and letters.</p>
                        </div>
                    </div>

                    <div className="my-6 flex justify-center">
                        {settings.company_logo ? (
                            <div className="relative group w-32 h-32 border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden flex items-center justify-center bg-white p-2">
                                <img src={settings.company_logo} alt="Logo" className="max-w-full max-h-full object-contain" />
                                <button
                                    onClick={() => handleRemoveFile("company_logo")}
                                    className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition rounded-2xl"
                                >
                                    <Trash2 className="w-5 h-5 text-rose-500" />
                                </button>
                            </div>
                        ) : (
                            <label className="w-32 h-32 border-2 border-dashed border-slate-300 dark:border-white/10 hover:border-primary/50 cursor-pointer rounded-2xl flex flex-col items-center justify-center gap-2 text-muted-foreground transition">
                                <Upload className="w-5 h-5" />
                                <span className="text-[11px] font-medium">Upload File</span>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleFileChange(e, "company_logo")}
                                    className="hidden"
                                />
                            </label>
                        )}
                    </div>
                </div>

                {/* Founder Signature */}
                <div className="glass p-6 rounded-2xl border border-slate-200 dark:border-white/10 flex flex-col justify-between shadow-sm">
                    <div className="space-y-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary grid place-items-center">
                            <FileSignature className="w-5 h-5" />
                        </div>
                        <div>
                            <h4 className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Founder Signature</h4>
                            <p className="text-xs text-muted-foreground mt-0.5">Official signature used on all letters and LORs.</p>
                        </div>
                    </div>

                    <div className="my-6 flex justify-center">
                        {settings.founder_signature ? (
                            <div className="relative group w-32 h-32 border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden flex items-center justify-center bg-white p-2">
                                <img src={settings.founder_signature} alt="Signature" className="max-w-full max-h-full object-contain" />
                                <button
                                    onClick={() => handleRemoveFile("founder_signature")}
                                    className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition rounded-2xl"
                                >
                                    <Trash2 className="w-5 h-5 text-rose-500" />
                                </button>
                            </div>
                        ) : (
                            <label className="w-32 h-32 border-2 border-dashed border-slate-300 dark:border-white/10 hover:border-primary/50 cursor-pointer rounded-2xl flex flex-col items-center justify-center gap-2 text-muted-foreground transition">
                                <Upload className="w-5 h-5" />
                                <span className="text-[11px] font-medium">Upload File</span>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleFileChange(e, "founder_signature")}
                                    className="hidden"
                                />
                            </label>
                        )}
                    </div>
                </div>

                {/* Company Seal */}
                <div className="glass p-6 rounded-2xl border border-slate-200 dark:border-white/10 flex flex-col justify-between shadow-sm">
                    <div className="space-y-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary grid place-items-center">
                            <Shield className="w-5 h-5" />
                        </div>
                        <div>
                            <h4 className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Company Seal</h4>
                            <p className="text-xs text-muted-foreground mt-0.5">Corporate stamp to establish document validity.</p>
                        </div>
                    </div>

                    <div className="my-6 flex justify-center">
                        {settings.company_seal ? (
                            <div className="relative group w-32 h-32 border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden flex items-center justify-center bg-white p-2">
                                <img src={settings.company_seal} alt="Company Seal" className="max-w-full max-h-full object-contain" />
                                <button
                                    onClick={() => handleRemoveFile("company_seal")}
                                    className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition rounded-2xl"
                                >
                                    <Trash2 className="w-5 h-5 text-rose-500" />
                                </button>
                            </div>
                        ) : (
                            <label className="w-32 h-32 border-2 border-dashed border-slate-300 dark:border-white/10 hover:border-primary/50 cursor-pointer rounded-2xl flex flex-col items-center justify-center gap-2 text-muted-foreground transition">
                                <Upload className="w-5 h-5" />
                                <span className="text-[11px] font-medium">Upload File</span>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleFileChange(e, "company_seal")}
                                    className="hidden"
                                />
                            </label>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
}
